import * as React from 'react';
import { createPortal } from 'react-dom';

interface DialogContextValue {
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

const DialogContext = React.createContext<DialogContextValue | undefined>(undefined);

function useDialog() {
    const context = React.useContext(DialogContext);
    if (!context) {
        throw new Error('Dialog components must be used within Dialog');
    }
    return context;
}

export function Dialog({
    open,
    onOpenChange,
    children
}: {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    children: React.ReactNode;
}) {
    return (
        <DialogContext.Provider value={{ open, onOpenChange }}>
            {children}
        </DialogContext.Provider>
    );
}

export function DialogPortal({ children }: { children: React.ReactNode }) {
    const { open } = useDialog();
    if (!open) return null;
    return createPortal(children, document.body);
}

export function DialogOverlay({
    className = '',
    ...props
}: React.HTMLAttributes<HTMLDivElement>) {
    const { onOpenChange } = useDialog();

    return (
        <div
            className={`fixed inset-0 z-50 bg-black/60 backdrop-blur-sm ${className}`}
            onClick={() => onOpenChange(false)}
            {...props}
        />
    );
}

export function DialogContent({
    className = '',
    children,
    ...props
}: React.HTMLAttributes<HTMLDivElement>) {
    const { onOpenChange } = useDialog();

    React.useEffect(() => {
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                onOpenChange(false);
            }
        };
        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [onOpenChange]);

    return (
        <div
            className={`fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2 w-[90vw] max-w-3xl max-h-[90vh] rounded-xl border shadow-2xl bg-white text-slate-900 border-slate-200 dark:bg-slate-950 dark:text-slate-50 dark:border-slate-800 overflow-hidden flex flex-col ${className}`}
            onClick={(e) => e.stopPropagation()}
            {...props}
        >
            {children}
        </div>
    );
}

export function DialogHeader({
    className = '',
    ...props
}: React.HTMLAttributes<HTMLDivElement>) {
    return <div className={`px-6 py-4 border-b border-slate-200 dark:border-slate-800 flex-shrink-0 ${className}`} {...props} />;
}

export function DialogTitle({
    className = '',
    ...props
}: React.HTMLAttributes<HTMLHeadingElement>) {
    return <h2 className={`h5 ${className}`} {...props} />;
}

export function DialogFooter({
    className = '',
    ...props
}: React.HTMLAttributes<HTMLDivElement>) {
    return <div className={`px-6 py-4 border-t border-slate-200 dark:border-slate-800 flex-shrink-0 ${className}`} {...props} />;
}

export function DialogDescription({
    className = '',
    ...props
}: React.HTMLAttributes<HTMLParagraphElement>) {
    return <p className={`text-slate-500 dark:text-slate-400 text-sm ${className}`} {...props} />;
}

export function DialogClose({
    className = '',
    ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement>) {
    const { onOpenChange } = useDialog();

    return (
        <button
            type="button"
            onClick={() => onOpenChange(false)}
            className={`${className}`}
            {...props}
        />
    );
}
